import type { ComparePageCopy, ComparePageCopyByLang } from "./compare";

const VENDOR = { name: "Zoom AI Companion", url: "https://www.zoom.com/en/products/ai-assistant/" };

const en: ComparePageCopy = {
  seo: {
    title: "Zoom AI Companion vs. Minuta — cross-platform meeting AI without lock-in",
    description:
      "Zoom AI Companion only works in Zoom. Minuta works in Zoom, Teams, Meet, and any meeting on your computer — with no bot in the call, fast answers in English, Portuguese, and Spanish, and native PT-BR and ES interfaces.",
    keywords:
      "zoom ai companion alternative, zoom ai vs minuta, cross-platform meeting ai, secure meeting copilot, microsoft teams meeting copilot, google meet copilot",
  },
  vendor: VENDOR,
  eyebrow: "Comparison",
  h1: "Zoom AI Companion vs. Minuta",
  intro:
    "Zoom AI Companion is bundled into Zoom Workplace. It only runs in Zoom. Minuta runs in every meeting you join — Zoom, Teams, Meet, anything that produces audio on your computer. Here's where each one wins.",
  honesty: {
    heading: "Pick the right one",
    pickThem: {
      name: "Zoom AI Companion",
      bullets: [
        "Your entire team and every customer is on Zoom.",
        "You're already paying for Zoom Workplace and the AI is bundled — free.",
        "You do not mind that audio and AI processing run on Zoom infrastructure.",
        "Your meetings are mostly internal and a formulaic summary is enough.",
      ],
    },
    pickMinuta: [
      "Your team uses 2+ video platforms — Zoom plus Meet plus Teams plus a browser dial-in.",
      "You need a meeting copilot that works in Zoom AND Teams AND Meet — not just one of them.",
      "You speak Portuguese or Spanish and want a real-time copilot in your language.",
      "You want a meeting AI that's purpose-built — not a feature bolted onto a video product.",
    ],
  },
  table: {
    heading: "Side by side",
    headers: { feature: "Feature", them: "Zoom AI Companion", minuta: "Minuta" },
    rows: [
      {
        feature: "Works in Zoom",
        them: { value: "Yes — native" },
        minuta: { value: "Yes" },
      },
      {
        feature: "Works in Microsoft Teams",
        them: {
          value: "No",
          cite: { url: "https://www.zoom.com/en/products/ai-assistant/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Yes" },
      },
      {
        feature: "Works in Google Meet",
        them: {
          value: "No",
          cite: { url: "https://www.zoom.com/en/products/ai-assistant/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Yes" },
      },
      {
        feature: "Works in browser dial-ins",
        them: { value: "No" },
        minuta: { value: "Yes" },
      },
      {
        feature: "Bot in the meeting",
        them: { value: "No — host-side, native" },
        minuta: { value: "No — local capture" },
      },
      {
        feature: "Audio processing",
        them: {
          value: "Zoom cloud infrastructure",
          cite: { url: "https://news.zoom.com/zoom-ai-companion/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Local capture, secure transient API" },
      },
      {
        feature: "Real-time answer copilot",
        them: { value: "No — formulaic post-call summary" },
        minuta: { value: "Yes (Pro)" },
      },
      {
        feature: "Live translation (EN ↔ PT/ES)",
        them: { value: "Cloud translation in Zoom only" },
        minuta: { value: "Yes — real-time across Zoom, Teams, Meet" },
      },
      {
        feature: "Custom context (PDFs/notes)",
        them: { value: "Limited" },
        minuta: { value: "Yes — copilot uses your docs" },
      },
      {
        feature: "Native PT/ES website",
        them: { value: "n/a (Zoom site, not Zoom AI-specific)" },
        minuta: { value: "Yes" },
      },
      {
        feature: "Free tier",
        them: {
          value: "Bundled in any paid Zoom plan; AI Companion Basic on Zoom Basic",
          cite: { url: "https://news.zoom.com/zoom-ai-companion/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "7-day full trial · then 1h/month Free" },
      },
      {
        feature: "Paid",
        them: {
          value: "$8.33/u/mo annual standalone",
          cite: { url: "https://www.zoom.com/en/products/ai-assistant/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "€9/mo" },
      },
    ],
    footnote: "Pricing as of 2026-04-30; check each vendor for the current numbers.",
  },
  whenMinuta: {
    heading: "Where Minuta wins",
    bullets: [
      "Cross-platform. Real teams use Zoom + Meet + Teams. Zoom AI Companion only works in Zoom. We work everywhere your computer plays audio.",
      "Clear data path. Zoom AI Companion runs on Zoom infrastructure. Minuta captures audio locally, sends only the needed meeting data through secure transient cloud APIs, and stores your files on your computer.",
      "Purpose-built. Incumbent meeting AI is bolted onto a video product and tends to be mediocre and complacent — Zoom's summaries are formulaic. Minuta is built for the moment a question lands.",
      "Native PT and ES. Zoom translates everything via the cloud. We ship native PT-BR and ES sites and DeepL-backed translation when configured.",
    ],
  },
  also: { heading: "Comparing other tools?", links: [] },
  cta: {
    heading: "Try the cross-platform meeting AI",
    body: "7 days with everything unlocked. No credit card. Works in any meeting.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: ComparePageCopy = {
  seo: {
    title: "Zoom AI Companion vs. Minuta — IA de reunião em qualquer plataforma, sem lock-in",
    description:
      "Zoom AI Companion só roda no Zoom. Minuta roda no Zoom, no Teams, no Meet e em qualquer reunião no seu computador — sem bot na call, respostas rápidas em inglês, português e espanhol e interfaces nativas PT-BR e ES.",
    keywords:
      "alternativa zoom ai companion, zoom ai vs minuta, ia de reunião multi-plataforma, copiloto de reunião sem bot, copiloto microsoft teams, copiloto google meet",
  },
  vendor: VENDOR,
  eyebrow: "Comparação",
  h1: "Zoom AI Companion vs. Minuta",
  intro:
    "Zoom AI Companion vem incluso no Zoom Workplace. Só roda no Zoom. Minuta roda em qualquer reunião que entra — Zoom, Teams, Meet, qualquer coisa que toca áudio no seu computador. Onde cada um ganha:",
  honesty: {
    heading: "Escolha o certo",
    pickThem: {
      name: "Zoom AI Companion",
      bullets: [
        "O time inteiro e todo cliente estão no Zoom.",
        "Você já paga Zoom Workplace e a IA vem junto — de graça.",
        "Você não se importa que áudio e IA rodem na infraestrutura do Zoom.",
        "As reuniões são internas e um resumo padrão é suficiente.",
      ],
    },
    pickMinuta: [
      "O time usa 2+ plataformas — Zoom + Meet + Teams + dial-in pelo navegador.",
      "Você precisa de um copiloto de reuniões que funcione no Zoom E no Teams E no Meet — não só em uma delas.",
      "Você fala português ou espanhol e quer copiloto em tempo real na sua língua.",
      "Você quer IA de reunião que foi feita pra isso — não uma feature parafusada num produto de vídeo.",
    ],
  },
  table: {
    heading: "Lado a lado",
    headers: { feature: "Recurso", them: "Zoom AI Companion", minuta: "Minuta" },
    rows: [
      {
        feature: "Funciona no Zoom",
        them: { value: "Sim — nativo" },
        minuta: { value: "Sim" },
      },
      {
        feature: "Funciona no Microsoft Teams",
        them: {
          value: "Não",
          cite: { url: "https://www.zoom.com/en/products/ai-assistant/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Sim" },
      },
      {
        feature: "Funciona no Google Meet",
        them: {
          value: "Não",
          cite: { url: "https://www.zoom.com/en/products/ai-assistant/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Sim" },
      },
      {
        feature: "Funciona em dial-in via navegador",
        them: { value: "Não" },
        minuta: { value: "Sim" },
      },
      {
        feature: "Bot na reunião",
        them: { value: "Não — host-side, nativo" },
        minuta: { value: "Não — captura local de áudio" },
      },
      {
        feature: "Processamento do áudio",
        them: {
          value: "Infraestrutura de nuvem do Zoom",
          cite: { url: "https://news.zoom.com/zoom-ai-companion/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Captura local, API temporária segura" },
      },
      {
        feature: "Copiloto sugerindo resposta em tempo real",
        them: { value: "Não — resumo pós-call padronizado" },
        minuta: { value: "Sim (Pro)" },
      },
      {
        feature: "Tradução ao vivo (EN ↔ PT/ES)",
        them: { value: "Tradução em nuvem só dentro do Zoom" },
        minuta: { value: "Sim — tempo real no Zoom, Teams e Meet" },
      },
      {
        feature: "Contexto seu (PDFs/notas)",
        them: { value: "Limitado" },
        minuta: { value: "Sim — copiloto usa seus docs" },
      },
      {
        feature: "Site nativo em PT/ES",
        them: { value: "n/a (site do Zoom, não específico do AI)" },
        minuta: { value: "Sim" },
      },
      {
        feature: "Plano grátis",
        them: {
          value: "Incluso em plano Zoom pago; AI Companion Basic no Zoom Basic",
          cite: { url: "https://news.zoom.com/zoom-ai-companion/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "7 dias com tudo · depois 1h/mês no Free" },
      },
      {
        feature: "Pago",
        them: {
          value: "US$ 8,33/u/mês anual standalone",
          cite: { url: "https://www.zoom.com/en/products/ai-assistant/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "€9/mês" },
      },
    ],
    footnote: "Preços referência 2026-04-30; confirme com cada empresa pra valores atuais.",
  },
  whenMinuta: {
    heading: "Onde o Minuta ganha",
    bullets: [
      "Multi-plataforma. Time real usa Zoom + Meet + Teams. Zoom AI Companion só funciona no Zoom. A gente funciona onde o seu computador toca áudio.",
      "Caminho de dados claro. Zoom AI Companion roda na infraestrutura do Zoom. O Minuta captura o áudio localmente, envia só os dados necessários por APIs de nuvem transientes e salva seus arquivos no computador.",
      "Feito pra reunião. IA de reunião dos incumbentes é parafusada num produto de vídeo e tende a ser medíocre — os resumos do Zoom são padronizados. Minuta foi feito pro momento em que cai a pergunta.",
      "PT e ES nativos. O Zoom traduz tudo via nuvem. A gente tem site nativo PT-BR e ES e tradução via DeepL quando configurado.",
    ],
  },
  also: { heading: "Comparando outras ferramentas?", links: [] },
  cta: {
    heading: "Testa a IA de reunião multi-plataforma",
    body: "7 dias com tudo desbloqueado. Sem cartão. Funciona em qualquer reunião.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: ComparePageCopy = {
  seo: {
    title: "Zoom AI Companion vs. Minuta — IA de reunión en cualquier plataforma, sin lock-in",
    description:
      "Zoom AI Companion solo corre en Zoom. Minuta corre en Zoom, Teams, Meet y en cualquier reunión en tu computadora — sin bot en la llamada, respuestas rápidas en inglés, portugués y español e interfaces nativas PT-BR y ES.",
    keywords:
      "alternativa zoom ai companion, zoom ai vs minuta, ia de reunión multi-plataforma, copiloto de reunión sin bot, copiloto microsoft teams, copiloto google meet",
  },
  vendor: VENDOR,
  eyebrow: "Comparación",
  h1: "Zoom AI Companion vs. Minuta",
  intro:
    "Zoom AI Companion viene incluido en Zoom Workplace. Solo corre en Zoom. Minuta corre en cualquier reunión a la que entres — Zoom, Teams, Meet, lo que sea que reproduzca audio en tu computadora. Dónde gana cada uno:",
  honesty: {
    heading: "Elige el correcto",
    pickThem: {
      name: "Zoom AI Companion",
      bullets: [
        "Todo tu equipo y cada cliente está en Zoom.",
        "Ya pagas Zoom Workplace y la IA viene incluida — gratis.",
        "No te molesta que el audio y la IA corran en la infraestructura de Zoom.",
        "Las reuniones son internas y un resumen estándar es suficiente.",
      ],
    },
    pickMinuta: [
      "Tu equipo usa 2+ plataformas — Zoom + Meet + Teams + dial-in por navegador.",
      "Necesitas un copiloto de reuniones que funcione en Zoom Y en Teams Y en Meet — no solo en una de ellas.",
      "Hablas español o portugués y quieres copiloto en tiempo real en tu idioma.",
      "Quieres IA de reunión hecha para eso — no una feature atornillada en un producto de vídeo.",
    ],
  },
  table: {
    heading: "Lado a lado",
    headers: { feature: "Función", them: "Zoom AI Companion", minuta: "Minuta" },
    rows: [
      {
        feature: "Funciona en Zoom",
        them: { value: "Sí — nativo" },
        minuta: { value: "Sí" },
      },
      {
        feature: "Funciona en Microsoft Teams",
        them: {
          value: "No",
          cite: { url: "https://www.zoom.com/en/products/ai-assistant/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Sí" },
      },
      {
        feature: "Funciona en Google Meet",
        them: {
          value: "No",
          cite: { url: "https://www.zoom.com/en/products/ai-assistant/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Sí" },
      },
      {
        feature: "Funciona en dial-in por navegador",
        them: { value: "No" },
        minuta: { value: "Sí" },
      },
      {
        feature: "Bot en la reunión",
        them: { value: "No — host-side, nativo" },
        minuta: { value: "No — captura local" },
      },
      {
        feature: "Procesamiento del audio",
        them: {
          value: "Infraestructura de nube de Zoom",
          cite: { url: "https://news.zoom.com/zoom-ai-companion/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Captura local, API transitoria segura" },
      },
      {
        feature: "Copiloto sugiriendo respuesta en tiempo real",
        them: { value: "No — resumen post-call estandarizado" },
        minuta: { value: "Sí (Pro)" },
      },
      {
        feature: "Traducción en vivo (EN ↔ PT/ES)",
        them: { value: "Traducción en nube solo dentro de Zoom" },
        minuta: { value: "Sí — tiempo real en Zoom, Teams y Meet" },
      },
      {
        feature: "Contexto propio (PDFs/notas)",
        them: { value: "Limitado" },
        minuta: { value: "Sí — el copiloto usa tus docs" },
      },
      {
        feature: "Web nativa en PT/ES",
        them: { value: "n/a (web de Zoom, no específica del AI)" },
        minuta: { value: "Sí" },
      },
      {
        feature: "Plan gratis",
        them: {
          value: "Incluido en cualquier plan Zoom de pago; AI Companion Basic en Zoom Basic",
          cite: { url: "https://news.zoom.com/zoom-ai-companion/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "7 días con todo · luego 1h/mes en Gratis" },
      },
      {
        feature: "De pago",
        them: {
          value: "US$ 8,33/u/mes anual standalone",
          cite: { url: "https://www.zoom.com/en/products/ai-assistant/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "€9/mes" },
      },
    ],
    footnote: "Precios referencia 2026-04-30; confirma con cada empresa los valores actuales.",
  },
  whenMinuta: {
    heading: "Dónde gana Minuta",
    bullets: [
      "Multi-plataforma. Equipo real usa Zoom + Meet + Teams. Zoom AI Companion solo funciona en Zoom. Nosotros funcionamos donde tu computadora reproduzca audio.",
      "Ruta de datos clara. Zoom AI Companion corre en infraestructura de Zoom. Minuta captura audio localmente, envía solo los datos necesarios por APIs de nube transitorias y guarda tus archivos en tu computadora.",
      "Hecho para reuniones. IA de reunión de los incumbentes está atornillada en un producto de vídeo y tiende a ser mediocre — los resúmenes de Zoom son estandarizados. Minuta se construyó para el momento en que llega la pregunta.",
      "PT y ES nativos. Zoom traduce todo por nube. Nosotros tenemos web nativa PT-BR y ES y traducción vía DeepL cuando está configurado.",
    ],
  },
  also: { heading: "¿Comparando otras herramientas?", links: [] },
  cta: {
    heading: "Prueba la IA de reunión multi-plataforma",
    body: "7 días con todo desbloqueado. Sin tarjeta. Funciona en cualquier reunión.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const compareZoomAiCompanionCopy: ComparePageCopyByLang = { en, pt, es };
